/**
 * Rôles IAM alignés sur `uruganda_core.iam.roles` (plateforme).
 * Matrice Entrea : rôles **génériques** (`executive`, `finance_lead`, …) ; les anciennes
 * valeurs (`président`, `membre`, `member`, …) sont normalisées pour les permissions.
 */

/**
 * Harmonise les variantes (ASCII, anglais, héritage FR) vers les clés matrice `usePermissions`.
 * @param {string | null | undefined} roleRaw
 * @returns {string}
 */
export function normalizeRoleKeyForPermissions(roleRaw) {
  const r = String(roleRaw || '')
    .toLowerCase()
    .trim();
  const map = {
    president: 'executive',
    président: 'executive',
    tresorier: 'finance_lead',
    treasurer: 'finance_lead',
    trésorier: 'finance_lead',
    secretaire: 'operations_lead',
    secretary: 'operations_lead',
    secrétaire: 'operations_lead',
    member: 'participant',
    membre: 'participant',
  };
  return map[r] || r;
}

/** Rôles à périmètre étendu type bureau (hors simple participant). */
export function isBoardOfficerRole(role) {
  if (role == null || role === '') return false;
  const n = normalizeRoleKeyForPermissions(role);
  return n === 'executive' || n === 'finance_lead' || n === 'operations_lead';
}

/** Rôle effectif « participant » (après normalisation IAM ; `member` → `participant`). */
export function isMemberEffectiveRole(role) {
  if (role == null || role === '') return false;
  return normalizeRoleKeyForPermissions(role) === 'participant';
}

/**
 * Expérience « portail membre » : participant, ou rôle bureau lié à une fiche membre
 * (aligné hors `/portal` avec usePermissions / DashboardLayout).
 */
export function isMemberLikeEntreaUser(role, hasMemberProfileLink) {
  return (
    isMemberEffectiveRole(role) ||
    (isBoardOfficerRole(role) && Boolean(hasMemberProfileLink))
  );
}

/** Administrateur d'organisation (console plateforme + droits étendus dans l'app) */
export function isOrgAdminRole(role) {
  if (role == null || role === '') return false;
  const r = String(role).toLowerCase();
  return r === 'admin' || r === 'org_admin' || r === 'organization_admin' || r === 'admin_project';
}

/** Org admin console ou administrateur plateforme SaaS. */
export function isOrgOrSaasAdminRole(role) {
  return isOrgAdminRole(role) || isSaasAdminRole(role);
}

/** Administrateur plateforme (hors périmètre org client courant) */
export function isSaasAdminRole(role) {
  if (role == null || role === '') return false;
  return String(role).toLowerCase() === 'saas_admin';
}

/** Manager organisation (pas la console plateforme globale) */
export function isOrgManagerRole(role) {
  if (role == null || role === '') return false;
  const r = String(role).toLowerCase();
  return r === 'manager' || r === 'org_manager';
}

/** Administrateur projet (front projet uniquement) */
export function isProjectAdminRole(role) {
  if (role == null || role === '') return false;
  return String(role).toLowerCase() === 'admin_project';
}

/** Libellé français pour affichage (liste utilisateurs, badges) */
export function formatIamRoleLabel(role) {
  if (role == null || role === '') return '—';
  const r = String(role).toLowerCase();
  const map = {
    saas_admin: 'Administrateur plateforme',
    org_admin: 'Administrateur organisation',
    admin_project: 'Administrateur projet',
    org_manager: 'Manager organisation',
    admin: 'Administrateur organisation',
    organization_admin: 'Administrateur organisation',
    executive: 'Rôle exécutif',
    président: 'Président',
    president: 'Président',
    finance_lead: 'Responsable finances',
    trésorier: 'Trésorier',
    treasurer: 'Trésorier',
    operations_lead: 'Responsable opérations',
    secrétaire: 'Secrétaire',
    secretary: 'Secrétaire',
    compliance_officer: 'Membre suivi & adhésion',
    auditor: 'Membre surveillance',
    participant: 'Participant',
    membre: 'Membre',
    member: 'Membre',
    manager: 'Manager organisation',
  };
  return map[r] || role;
}

/**
 * Rôle principal dérivé d'une position projet (premier `role_keys` non vide).
 * Renvoie `null` si la position n'a aucune clé de rôle exploitable.
 */
export function primaryRoleKeyFromPosition(position) {
  if (!position || typeof position !== 'object') return null;
  const keys = Array.isArray(position.role_keys) ? position.role_keys : [];
  for (const raw of keys) {
    const k = String(raw || '').trim();
    if (k) return k;
  }
  return null;
}

/**
 * Détermine le rôle IAM utilisateur correspondant à une position (id ou row).
 * Si aucune position n'est passée / trouvée, retourne `fallback`.
 */
export function resolveUserRoleFromPosition(positionOrId, positions, fallback = 'participant') {
  const list = Array.isArray(positions) ? positions : [];
  let pos = null;
  if (positionOrId && typeof positionOrId === 'object') {
    pos = positionOrId;
  } else if (positionOrId != null && String(positionOrId).trim() !== '') {
    const pid = String(positionOrId).trim();
    pos = list.find((p) => String(p?.id || '').trim() === pid) || null;
  }
  const primary = primaryRoleKeyFromPosition(pos);
  if (!primary) return fallback;
  return normalizeUserRoleForForm(primary);
}

/** Valeur pour les `<select>` utilisateur : mappe les anciens codes vers org_admin / org_manager */
export function normalizeUserRoleForForm(role) {
  if (role == null || role === '') return 'participant';
  const r = String(role).toLowerCase();
  if (r === 'admin' || r === 'organization_admin') return 'org_admin';
  if (r === 'manager') return 'org_manager';
  if (r === 'member' || r === 'membre') return 'participant';
  const g = normalizeRoleKeyForPermissions(role);
  if (g && g !== String(role || '').toLowerCase().trim()) return g;
  return role;
}
